import { useCallback } from "react"
import type { Stock } from "@stockline/shared/types/inventory/stock"
import { STOCK_TABLE } from "../../../lib/offline/resources/inventory/stock/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface CreateStockInput {
  productId: string
  quantity: number
  weight: number
  unitId?: string | null
  name?: string | null
  partnerId?: string | null
  lotId?: string | null
  locationId?: string | null
  qualityScore?: number | null
  price?: number | null
  stockMetadata?: Record<string, unknown> | null
}

export interface UpdateStockInput {
  productId?: string
  quantity?: number
  weight?: number
  name?: string | null
  partnerId?: string | null
  lotId?: string | null
  locationId?: string | null
  qualityScore?: number | null
  price?: number | null
  stockMetadata?: Record<string, unknown> | null
}

export function useStockMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createStock = useCallback(
    async (input: CreateStockInput): Promise<Stock> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      const stock: Stock = {
        id,
        companyId: "pending",
        productId: input.productId,
        unitId: input.unitId ?? null,
        quantity: input.quantity,
        weight: input.weight,
        name: input.name ?? null,
        partnerId: input.partnerId ?? null,
        lotId: input.lotId ?? null,
        locationId: input.locationId ?? null,
        qualityScore: input.qualityScore ?? null,
        price: input.price ?? null,
        stockMetadata: input.stockMetadata ?? null,
        syncId: 0,
        deletedAt: null,
        createdAt: now,
        updatedAt: now,
      }

      await storage.put(STOCK_TABLE, stock)
      await changeQueue.enqueue("stocks", {
        op: "create",
        id,
        data: stock,
      })
      void engine.pushChanges("stocks")

      return stock
    },
    [storage, changeQueue, engine]
  )

  const updateStock = useCallback(
    async (id: string, input: UpdateStockInput): Promise<void> => {
      const existing = await storage.get<Stock>(STOCK_TABLE, id)
      if (!existing) return

      const updated: Stock = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(STOCK_TABLE, updated)
      await changeQueue.enqueue("stocks", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("stocks")
    },
    [storage, changeQueue, engine]
  )

  const deleteStock = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(STOCK_TABLE, id)
      await changeQueue.enqueue("stocks", {
        op: "delete",
        id,
      })
      void engine.pushChanges("stocks")
    },
    [storage, changeQueue, engine]
  )

  return { createStock, updateStock, deleteStock }
}
